import {Random} from "@dp-instant/utils";
import {math} from "@game/coregame/helpers";
import {BuildingModel} from "@game/coregame/interfaces/BuildingModel";
import {Command} from "@game/coregame/interfaces/Command";
import {DecisionType} from "@game/coregame/interfaces/DecisionType";
import {IBotConfig} from "@game/coregame/interfaces/IBotConfig";
import {PlayerType} from "@game/coregame/interfaces/PlayerType";
import {GameModel} from "@game/coregame/model/GameModel";
import {Population} from "@game/coregame/view/Population";
import {Spawner} from "@game/coregame/view/Spawner";
import {TypesGame} from "@game/TypesGame";
import {inject, injectable} from "inversify";
import {Building} from "../../entities/Building";
import {FighterGroupsSystem} from "../../systems/FighterGroupSystem";
import {PathHolder} from "../PathHolder";
import {BotUtility} from "./BotUtility";
import {BState} from "./BState";
import {CommandsGenerator} from "./CommandsGenerator";
import {GroupModel} from "./GroupModel";

const getById = <T extends {Id: string}>(items: T[], id: string): T | undefined => {
    for (const item of items) if (item.Id === id) return item;
};
const getKilledAmountFromSegment = (amount: float, otherAmount: float): float => {
    return Math.max(amount - otherAmount, 0);
};
const getKilledAmountFromTower = (amount: float, step: float, towerAttackRate: float): float => {
    const killedAmount = towerAttackRate * step;
    return math.clamp(killedAmount, 0, amount);
};
const getStateForces = (state: BState, owner: PlayerType) => {
    const isOwner = (a: {Owner: PlayerType}) => a.Owner === owner;
    const sum = (p: number, c: number) => c + p;
    return (
        state.buildings
            .filter(isOwner)
            .map(b => b.CurrentPopulation)
            .reduce(sum, 0) +
        state.groups
            .filter(isOwner)
            .map(g => g.Amount)
            .reduce(sum, 0)
    );
};
@injectable()
export class BotCalculationLogic {
    @inject(TypesGame.model)
    protected model: GameModel;
    private _calculationPromise?: Promise<void>;
    private _lastCommand?: Command;
    private _owner: PlayerType;
    private _preset: IBotConfig;
    private _initialWaitPromise?: Promise<void>;
    private _rejectInitialPromise?: () => void;

    init(preset: IBotConfig) {
        this._preset = preset;
    }
    public async calculate(building: Building): Promise<void> {
        const {owner} = building;
        // if (process.env.NODE_ENV === "development") {
        // const title = `#bot [${this._preset.name}]${building.stateId}`;
        // console.time(title);

        this._owner = owner;
        await (this._calculationPromise ||= this._calculate());
        delete this._calculationPromise;
        // console.timeEnd(title);
        // if (this._lastCommand) {
        //     log.trace(
        //         `#bot [${this._preset.name}]${building.stateId} decide to`,
        //         DecisionType[this._lastCommand.Type],
        //         this._lastCommand.Subject.Id,
        //         this._lastCommand.Objects.map(o => o.Id).join(",")
        //     );
        // }
    }
    private async _calculate(): Promise<void> {
        this._initialWaitPromise = new Promise<void>((resolve, reject) => {
            this._rejectInitialPromise = reject;
            setTimeout(resolve, Random.rangeFloat(0, 3000));
        });
        try {
            await this._initialWaitPromise;
        } catch (e: any) {
            return; // waiting promise were rejected;
        } finally {
            delete this._initialWaitPromise;
            delete this._rejectInitialPromise;
        }
        type DecisionScore = {index: int; score: float};
        const startState = this.generateStartState();
        const commands: Command[] = CommandsGenerator.GeneratePossibleCommands(
            startState,
            this._owner,
            this._preset
        );

        const decisions: DecisionScore[] = [];
        for (let index = 0; index < commands.length; index++) {
            const score = await this.calculateCommandScore(startState, commands[index]);
            decisions.push({index, score});
        }
        const orderedDecisions = decisions.sort((a, b) => b.score - a.score);
        const [min, max] = this._preset.commandInDecisionOrder;
        const choose = Random.range(
            Math.min(min, decisions.length - 1),
            Math.min(max, decisions.length)
        );
        /*log.trace(
            `#bot ${this._preset.name} thinks`,
            orderedDecisions.map(o => ({
                type: DecisionType[commands[o.index].Type],
                score: o.score,
                cmd: commands[o.index]
            }))
        );*/
        const {index: decisionIndex} = orderedDecisions[choose];
        this._lastCommand = commands[decisionIndex];
    }
    private async calculateCommandScore(startState: BState, command: Command): Promise<number> {
        const {traceDuration: duration, stepDuration: step} = this._preset;
        const updatedState = this.executeCommand(startState, command);
        let targetReachTime = NaN;
        for (let time = 0; time <= duration; time += step) {
            this.traceState(updatedState, step);
            if (!isNaN(targetReachTime)) continue;
            if (this.isTargetReached(updatedState, command)) {
                targetReachTime = time;
            }
        }
        let score = this.CalculateStateScore(startState, updatedState, targetReachTime);
        if (command.Type == DecisionType.Move) {
            if (command.Subject.Owner == PlayerType.Neutral) score *= 2;
        }

        return score;
    }
    private CalculateStateScore(
        startState: BState,
        endState: BState,
        targetReachTime: float
    ): float {
        const startForce = getStateForces(startState, this._owner);
        const endForce = getStateForces(endState, this._owner);
        const force = (endForce - startForce) / (startForce == 0 ? 1 : startForce);
        const forceWithFactor = force * this._preset.forcesBalanceFactor;

        const startBuildings = startState.buildings.filter(b => b.Owner == this._owner).length;
        const endBuildings = endState.buildings.filter(b => b.Owner == this._owner).length;
        const buildings =
            (endBuildings - startBuildings) / (startBuildings == 0 ? 1 : startBuildings);
        const buildingsWithFactor = buildings * this._preset.buildingsBalanceFactor;

        let sum = forceWithFactor + buildingsWithFactor;

        if (!isNaN(targetReachTime)) {
            var boost = 1 - math.remap(targetReachTime, 0, endState.timestamp, 0, 1);
            sum += Math.abs(sum) * this._preset.targetReachedTimeBoost * boost;
            // _debugger?.SetReachTime(targetReachTime, boost, _preset.TargetReachedTimeBoost * boost);
        } else {
            // _debugger?.SetReachTime(State.NOT_DEFINED, 0, _preset.TargetReachedTimeBoost * 0);
        }

        // _debugger?.SetScores(force, forceWithFactor, buildings, buildingsWithFactor, 0, 0);

        return sum;
    }
    private generateStartState(): BState {
        const buildingModels: BuildingModel[] = [];
        this.model.currentContinent.buildings.forEach(building => {
            if (building.hasTag(Building.ACTIVE_TAG)) {
                buildingModels.push(building.getSnapshot());
            }
        });
        const now = Date.now();
        const groups = FighterGroupsSystem.GetActiveGroups();
        const groupsModels: (GroupModel | undefined)[] = groups.map(group => {
            if (group.Source.has(Spawner)) {
                const newGroup = new GroupModel(
                    group.Owner,
                    group.Speed,
                    group.Amount,
                    group.Path,
                    group.Target.stateId,
                    group.Source.stateId,
                    0,
                    -1
                );

                newGroup.Init(
                    group.StartTimestamp,
                    (now - group.StartTimestamp) * group.Speed,
                    group.BurstDelay,
                    group.BurstWidth
                );
                return newGroup;
            }
        });

        return new BState(buildingModels, groupsModels.filter(g => !!g) as GroupModel[], now);
    }
    private moveState(state: BState, command: Command): BState {
        const timestamp = state.timestamp;
        const buildings: BuildingModel[] = state.buildings.slice(0);
        const groups: GroupModel[] = state.groups.slice(0);
        const buildingB = this.model.currentContinent.buildings.get(command.Subject.Id)!;

        command.Objects.forEach(obj => {
            const buildingA = this.model.currentContinent.buildings.get(obj.Id)!;
            const pathHolder = buildingA.get(PathHolder)!;
            const pathNode = pathHolder.getPath(buildingB.stateId)!;
            const world = pathNode.map(n => math.lerp(...n, 0.5));
            var startAmount = math.clamp(
                obj.CurrentPopulation - Spawner.UNITS_PER_WAVE,
                0,
                Spawner.UNITS_PER_WAVE
            );
            const Amount = obj.CurrentPopulation - startAmount;

            const newGroup = new GroupModel(
                obj.Owner,
                obj.FighterSpeed,
                Amount,
                world,
                command.Subject.Id,
                obj.Id,
                0,
                timestamp
            );

            newGroup.Init(state.timestamp, 0, obj.BurstDelay, obj.BurstWidth);
            groups.push(newGroup);
            obj.CurrentPopulation -= startAmount;
            obj.LastActionTimestamp = timestamp;

            for (let i = 0; i < buildings.length; i++) {
                if (buildings[i].Id === obj.Id) {
                    buildings[i] = obj;
                    break;
                }
            }
        });

        return new BState(buildings, groups, timestamp);
    }
    private traceState(state: BState, step: number): BState {
        const buildings = state.buildings.slice(0);
        const groups = state.groups.slice(0);
        const timestamp = state.timestamp + step;

        this.calculateGroupsMoving(buildings, groups, step);
        this.calculateBuildingsToGroupAmount(buildings, groups, timestamp);
        this.calculateGroupToBuildingsAmount(buildings, groups, timestamp);
        this.updateSpawnBuildings(buildings, timestamp, step);

        return new BState(buildings, groups, timestamp);
    }
    private calculateGroupsMoving(building: BuildingModel[], groups: GroupModel[], step: number) {
        for (const group of groups) {
            group.Amount = this.CalculateTowersIntersectionAmount(building, group, step);
            // Calculate moving
            group.AccumPath(group.Speed * step);
        }

        if (this._preset.isConsiderGroupToGroupIntersection) {
            const amounts = new Array(groups.length).fill(0);
            for (var index = 0; index < groups.length; index++) {
                amounts[index] = this.CalculateGroupsIntersectionAmount(
                    groups,
                    groups[index],
                    step
                );
            }

            for (var index = 0; index < groups.length; index++) {
                var group = groups[index];
                group.Amount = amounts[index];
                groups[index] = group;
            }
        }
    }
    private CalculateTowersIntersectionAmount(
        buildings: BuildingModel[],
        group: GroupModel,
        step: float
    ): float {
        var amount = group.Amount;

        for (const tower of buildings) {
            if (tower.Owner == group.Owner || tower.AttackRate <= 0) continue;

            const segments = group.GenerateSegments(this._preset.fightersInSegment);
            for (const seg of segments) {
                if (BotUtility.IsSegmentIntersectCircle(tower.Position, tower.AttackRadius, seg)) {
                    const killed = getKilledAmountFromTower(seg.Amount, step, tower.AttackRate);
                    amount -= getKilledAmountFromTower(seg.Amount, step, tower.AttackRate);
                }
            }
        }

        return amount;
    }
    private CalculateGroupsIntersectionAmount(
        groups: GroupModel[],
        group: GroupModel,
        step: float
    ): float {
        const segments = group.GenerateSegments(this._preset.fightersInSegment);

        for (const other of groups) {
            if (other.Owner == group.Owner || other.Amount <= 0) continue;

            var otherSegments = other.GenerateSegments(this._preset.fightersInSegment);
            for (var index = 0; index < segments.length; index++) {
                for (const otherSegment of otherSegments) {
                    if (BotUtility.IsSegmentIntersectSegment(segments[index], otherSegment)) {
                        var was = segments[index].Amount;
                        segments[index].Amount = getKilledAmountFromSegment(
                            segments[index].Amount,
                            other.Amount
                        );
                    }
                }
            }
        }

        return segments.map(s => s.Amount).reduce((p, c) => p + c, 0);
    }
    private calculateBuildingsToGroupAmount(
        buildings: BuildingModel[],
        groups: GroupModel[],
        timestamp: number
    ) {
        for (const group of groups) {
            if (timestamp - group.LastBurstTimestamp >= group.BurstDelay) {
                const sourceBuilding = getById(buildings, group.Source);
                if (!sourceBuilding) continue;

                if (sourceBuilding.CurrentPopulation <= 0 || sourceBuilding.Owner != group.Owner) {
                    group.TargetAmount = 0;
                }

                if (group.TargetAmount > 0) {
                    if (sourceBuilding.CurrentPopulation < group.TargetAmount)
                        group.TargetAmount = sourceBuilding.CurrentPopulation;

                    const startAmount = math.clamp(group.TargetAmount, 0, Spawner.UNITS_PER_WAVE);
                    group.Amount += startAmount;
                    group.TargetAmount -= startAmount;
                    group.LastBurstTimestamp = timestamp;
                    sourceBuilding.CurrentPopulation -= startAmount;
                    sourceBuilding.LastActionTimestamp = timestamp;
                }
            }
        }
    }
    private calculateGroupToBuildingsAmount(
        buildings: BuildingModel[],
        groups: GroupModel[],
        timestamp: number
    ) {
        for (const group of groups) {
            if (group.IsReachedEnd && group.Amount > 0) {
                const target = getById(buildings, group.Target);
                if (!target) continue;

                const amount = math.clamp(group.Amount, 0, Spawner.UNITS_PER_WAVE);
                group.Amount = Math.max(group.Amount - amount, 0);

                if (target.Owner == group.Owner) {
                    target.CurrentPopulation += amount;
                } else {
                    if (target.CurrentPopulation >= amount) {
                        target.CurrentPopulation -= amount;
                    } else {
                        // var downInfo = _buildings[target.Id].AttackedStateInfo(group.Owner);
                        target.Owner = group.Owner;
                        target.CurrentPopulation =
                            amount -
                            target.CurrentPopulation +
                            Population.SPAWN_AMOUNT_ON_OCCUPATION;
                        target.AttackRadius = 0; // downInfo.AttackRadius;
                        target.AttackRate = 0; //downInfo.AttackRate;
                        target.SpawnLimit = this.model.meta.getBuildingPopulationLimit(group.Owner);
                        target.SpawnRate = this.model.meta.getPopulationRate(group.Owner);
                    }
                }

                target.LastActionTimestamp = timestamp;
            }
        }
    }
    private updateSpawnBuildings(buildings: BuildingModel[], timestamp: number, step: number) {
        for (let i = 0; i < buildings.length; i++) {
            const building = buildings[i];
            const passed = timestamp - building.LastActionTimestamp;
            const last = Math.max(Population.BLOCK_POPULATION_SECONDS - passed, 0);
            const stepWithCooldown = Math.max(step - last, 0);
            const populationDelta = building.SpawnRate * stepWithCooldown;
            const amount = Math.min(
                building.CurrentPopulation + populationDelta,
                building.SpawnLimit
            );

            building.CurrentPopulation = amount;
            buildings[i] = building;
        }
    }
    private isTargetReached(updatedState: BState, command: Command): boolean {
        switch (command.Type) {
            case DecisionType.Upgrade:
            case DecisionType.MakeTower:
            case DecisionType.MakeBuilding:
            case DecisionType.Wait:
                return true;

            case DecisionType.Move:
                const groups = updatedState.groups.slice(0);
                const IsBuildingInCommand = (buildingId: string): bool => {
                    for (const obj of command.Objects) {
                        if (obj.Id === buildingId) {
                            return true;
                        }
                    }
                    return false;
                };

                const thisMovedGroups = groups.filter(
                    g =>
                        g.Owner == this._owner &&
                        IsBuildingInCommand(g.Source) &&
                        g.Target == command.Subject.Id &&
                        g.CreationTimestamp == 0
                );
                for (const group of thisMovedGroups)
                    if (group.IsReachedEnd) {
                        return true;
                    }
                return false;

            default:
                throw new Error("ArgumentOutOfRangeException()");
        }
    }
    private executeCommand(state: BState, command: Command): BState {
        switch (command.Type) {
            case DecisionType.Move:
                return this.moveState(state, command);
            case DecisionType.Wait:
                return state.clone();
            default:
                return state;
        }
    }

    public getLastCommand() {
        const command = this._lastCommand;
        delete this._lastCommand;
        return command;
    }
    public get busy(): boolean {
        return !!this._calculationPromise;
    }

    public terminate() {
        this._rejectInitialPromise?.call(null);
    }
}
